import type { HumNote } from '../types';

export function getPlaybackDurationSeconds(notes: HumNote[], condensed: boolean): number {
  if (notes.length === 0) return 0;
  if (condensed) {
    return notes.length * 0.5;
  }
  let totalSec = 0;
  for (let i = 0; i < notes.length; i++) {
    const note = notes[i];
    let dur = (note.duration || 300) / 1000;
    dur = Math.max(0.12, Math.min(3.0, dur));
    totalSec += dur;

    if (i + 1 < notes.length) {
      const nextNote = notes[i + 1];
      const noteEndTimestamp = note.timestamp + (note.duration || 300);
      const deltaMs = nextNote.timestamp - noteEndTimestamp;
      if (deltaMs > 100) {
        totalSec += deltaMs / 1000;
      }
    }
  }
  return totalSec;
}

export class SynthEngine {
  private audioCtx: AudioContext;
  private notes: HumNote[] = [];
  private isPlaying = false;
  private scheduledNodes: OscillatorNode[] = [];
  private gainNode: GainNode;
  private delayNode: DelayNode;
  private feedbackNode: GainNode;
  private wetNode: GainNode;
  private waveType: OscillatorType = 'sine';
  private volume = 0.3;
  private playResolve: (() => void) | null = null;
  private stoppedEarly = false;

  constructor(audioCtx: AudioContext) {
    this.audioCtx = audioCtx;
    this.gainNode = this.audioCtx.createGain();
    this.gainNode.gain.value = this.volume;

    const compressor = this.audioCtx.createDynamicsCompressor();
    compressor.threshold.value = -16;
    compressor.knee.value = 6;
    compressor.ratio.value = 4;
    compressor.attack.value = 0.003;
    compressor.release.value = 0.25;

    this.gainNode.connect(compressor);
    compressor.connect(this.audioCtx.destination);

    // Atmospheric echo / reverb network for immersive acoustic space
    this.delayNode = this.audioCtx.createDelay();
    this.delayNode.delayTime.value = 0.28;
    this.feedbackNode = this.audioCtx.createGain();
    this.feedbackNode.gain.value = 0.35;
    this.wetNode = this.audioCtx.createGain();
    this.wetNode.gain.value = 0.22;

    this.delayNode.connect(this.feedbackNode);
    this.feedbackNode.connect(this.delayNode);
    this.delayNode.connect(this.wetNode);
    this.wetNode.connect(this.gainNode);
  }

  loadNotes(notes: HumNote[]): void {
    this.notes = [...notes];
  }

  setTimbre(type: OscillatorType): void {
    this.waveType = type;
  }

  setVolume(vol: number): void {
    this.volume = vol;
    this.gainNode.gain.setTargetAtTime(vol, this.audioCtx.currentTime, 0.01);
  }

  play(onNoteStart?: (index: number) => void, startIndex = 0, condensed = false): Promise<void> {
    if (this.notes.length === 0 || this.isPlaying) return Promise.resolve();
    this.isPlaying = true;
    this.stoppedEarly = false;
    this.scheduledNodes = [];

    return new Promise<void>((resolve) => {
      this.playResolve = resolve;
      let currentTime = this.audioCtx.currentTime + 0.1;
      let totalDuration = 0;
      const glideTime = 0.04;
      let prevFreq = 0;

      if (startIndex > 0) {
        for (let j = startIndex - 1; j >= 0; j--) {
          if (this.notes[j].frequency > 0) { prevFreq = this.notes[j].frequency; break; }
        }
      }

      for (let i = startIndex; i < this.notes.length; i++) {
        if (this.stoppedEarly) break;

        const note = this.notes[i];
        let duration: number;
        if (condensed) {
          duration = 0.5;
        } else if (note.duration && note.duration > 0) {
          duration = note.duration / 1000;
        } else if (i + 1 < this.notes.length) {
          duration = (this.notes[i + 1].timestamp - note.timestamp) / 1000;
        } else {
          duration = 0.4;
        }
        duration = Math.max(0.12, Math.min(3.0, duration));

        const freq = note.frequency;

        if (freq > 0) {
          const osc = this.audioCtx.createOscillator();
          const noteGain = this.audioCtx.createGain();

          osc.type = this.waveType;

          if (prevFreq === 0) {
            osc.frequency.setValueAtTime(freq, currentTime);
          } else {
            osc.frequency.setValueAtTime(prevFreq, currentTime);
            osc.frequency.linearRampToValueAtTime(freq, currentTime + glideTime);
          }
          prevFreq = freq;

          const ampScale = 0.3 + note.amplitude * 0.7;
          const attack = 0.012;
          const decay = 0.05;
          const sustainLevel = 0.75;
          const release = Math.min(0.06, duration * 0.35);
          const noteEnd = currentTime + duration;

          noteGain.gain.setValueAtTime(0, currentTime);
          noteGain.gain.linearRampToValueAtTime(ampScale, currentTime + attack);
          noteGain.gain.linearRampToValueAtTime(ampScale * sustainLevel, currentTime + attack + decay);
          noteGain.gain.setValueAtTime(ampScale * sustainLevel, Math.max(currentTime + attack + decay, noteEnd - release));
          noteGain.gain.linearRampToValueAtTime(0.0001, noteEnd);

           osc.connect(noteGain);
           noteGain.connect(this.gainNode);
           noteGain.connect(this.delayNode);

          osc.start(currentTime);
          osc.stop(noteEnd + 0.02);

          this.scheduledNodes.push(osc);

          const noteIndex = i;
          const startDelay = Math.max(0, (currentTime - this.audioCtx.currentTime) * 1000);
          setTimeout(() => {
            if (this.isPlaying) onNoteStart?.(noteIndex);
          }, startDelay);
        }

        let gapDuration = 0;
        if (!condensed && i + 1 < this.notes.length) {
          const nextNote = this.notes[i + 1];
          const noteEndTimestamp = note.timestamp + (note.duration || 300);
          const deltaMs = nextNote.timestamp - noteEndTimestamp;
          if (deltaMs > 100) {
            gapDuration = deltaMs / 1000;
          }
        }

        currentTime += duration + gapDuration;
        totalDuration = currentTime - this.audioCtx.currentTime;
      }

      setTimeout(() => {
        if (!this.stoppedEarly) {
          this.isPlaying = false;
          this.cleanup();
          this.playResolve = null;
          resolve();
        }
      }, totalDuration * 1000 + 200);
    });
  }

  stop(): void {
    this.stoppedEarly = true;
    this.isPlaying = false;
    this.cleanup();
    if (this.playResolve) {
      this.playResolve();
      this.playResolve = null;
    }
  }

  private cleanup(): void {
    for (const osc of this.scheduledNodes) {
      try { osc.stop(); } catch { /* already stopped */ }
    }
    this.scheduledNodes = [];
  }

  get playing(): boolean {
    return this.isPlaying;
  }

  playPreview(freq: number, duration: number): void {
    if (!this.audioCtx || this.audioCtx.state === 'closed') return;
    const osc = this.audioCtx.createOscillator();
    const g = this.audioCtx.createGain();
    osc.type = this.waveType;
    osc.frequency.setValueAtTime(freq, this.audioCtx.currentTime);
    g.gain.setValueAtTime(0, this.audioCtx.currentTime);
    g.gain.linearRampToValueAtTime(this.volume * 0.6, this.audioCtx.currentTime + 0.01);
    g.gain.linearRampToValueAtTime(0, this.audioCtx.currentTime + duration);
    osc.connect(g);
    g.connect(this.gainNode);
    g.connect(this.delayNode);
    osc.start(this.audioCtx.currentTime);
    osc.stop(this.audioCtx.currentTime + duration + 0.01);
  }

  playUIClick(): void {
    if (!this.audioCtx || this.audioCtx.state === 'closed') return;
    const osc = this.audioCtx.createOscillator();
    const g = this.audioCtx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(600, this.audioCtx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(150, this.audioCtx.currentTime + 0.025);
    g.gain.setValueAtTime(0.06, this.audioCtx.currentTime);
    g.gain.linearRampToValueAtTime(0, this.audioCtx.currentTime + 0.025);
    osc.connect(g);
    g.connect(this.gainNode);
    osc.start(this.audioCtx.currentTime);
    osc.stop(this.audioCtx.currentTime + 0.03);
  }
}
